/**
 * AwaitingCustomerWidget — approval requests still awaiting the customer, with
 * how long each has been waiting. Surfaces jobs stalled on customer sign-off so
 * they can be chased, shortening lead time.
 */
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { formatDistanceToNowStrict } from 'date-fns';
import type { AwaitingCustomerItem } from '@/hooks/useDashboard';
import { useOpenWorkOrder } from '@/hooks/useWorkOrderWindow';
import { dateFnsLocale } from '@/utils/locale';
import { handlePlainLeftClick } from './dashboardLinkClick';

interface AwaitingCustomerWidgetProps {
  items?: AwaitingCustomerItem[];
}

export function AwaitingCustomerWidget({ items = [] }: AwaitingCustomerWidgetProps) {
  const { t } = useTranslation('dashboard');
  const openWorkOrder = useOpenWorkOrder();
  const dateLocale = dateFnsLocale();

  const age = (createdAt: string | null): string => {
    if (!createdAt) return '';
    return formatDistanceToNowStrict(new Date(createdAt), { locale: dateLocale });
  };

  // Anything waiting over 24h is highlighted as overdue.
  const isOverdue = (createdAt: string | null): boolean =>
    !!createdAt && Date.now() - new Date(createdAt).getTime() > 24 * 60 * 60 * 1000;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <HourglassTopIcon color="secondary" />
          <Typography variant="h6">{t('awaitingCustomer.title')}</Typography>
          {items.length > 0 && (
            <Chip label={items.length} size="small" color="secondary" sx={{ ml: 'auto' }} />
          )}
        </Box>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('awaitingCustomer.empty')}
          </Typography>
        ) : (
          <List disablePadding>
            {items.map((item) => {
              const bookingId = item.bookingId;
              return (
              <ListItemButton
                key={item.id}
                component={RouterLink}
                to={bookingId ? `/workorders/${bookingId}` : '/approvals'}
                onClick={(event) => {
                  // No work order: follow the approvals link. A work order keeps
                  // the second-window hand-off on a plain left click (#4108).
                  if (!bookingId) return;
                  handlePlainLeftClick(event, () => openWorkOrder(bookingId));
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  px: 1,
                  gap: 1,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <Chip
                  label={age(item.createdAt)}
                  size="small"
                  color={isOverdue(item.createdAt) ? 'error' : 'default'}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.vehicleRegNumber || item.customerName}
                      </Typography>
                      {item.vehicleRegNumber && (
                        <Typography variant="body2" color="text.secondary">
                          {item.customerName}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    item.description ? (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {item.description}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItemButton>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
